// src/components/ContactForm.js
//
// The landing page's contact form. It posts to POST /api/v1/contact/, which
// sends the message to TrakLog from the server — the destination address is
// never part of the page. `website` is a honeypot: hidden off-screen, filled
// only by form bots, and the API drops any message that carries it.
// Cloudflare Turnstile guards it too; the API verifies the token (#788).
import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../apiConfig';
import { useTurnstile } from '../hooks/useTurnstile';

const TOPICS = [
  { value: 'pricing', label: 'Pricing' },
  { value: 'demo', label: 'A demo' },
  { value: 'other', label: 'Something else' },
];

const EMPTY = { name: '', email: '', company: '', topic: 'pricing', message: '', website: '' };

export const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

function ContactForm() {
  // The action must match TURNSTILE_ACTION in api/app/api/v1/endpoints/contact.py.
  const captcha = useTurnstile('contact');
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const emailValid = looksLikeEmail(form.email);
  const complete = form.name.trim() && emailValid && form.message.trim();

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!complete || sending) return;
    if (captcha.enabled && !captcha.token) {
      setError('The security check has not finished. Wait a moment, or complete the check above, then send again.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          topic: form.topic,
          message: form.message.trim(),
          website: form.website,
          turnstile_token: captcha.token,
        }),
      });
      if (res.ok) {
        setSent(true);
        setForm(EMPTY);
      } else if (res.status === 429) {
        setError('Too many messages from this connection. Please try again in an hour.');
      } else {
        setError('Your message could not be sent. Please try again later.');
      }
    } catch {
      setError('Your message could not be sent. Check your connection and try again.');
    } finally {
      // A token is single-use: whatever happened, the next attempt needs a new one.
      captcha.reset();
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Alert severity="success" sx={{ fontSize: '1rem' }}>
        Thanks. Your message is on its way. We&rsquo;ll reply to the email address you gave.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={submit} noValidate sx={{ position: 'relative' }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Your name" value={form.name} onChange={set('name')} required fullWidth
            autoComplete="name" inputProps={{ maxLength: 120 }}
            error={touched && !form.name.trim()}
          />
          <TextField
            label="Email" type="email" value={form.email} onChange={set('email')} required fullWidth
            autoComplete="email" inputProps={{ maxLength: 254 }}
            error={touched && !emailValid}
            helperText={touched && !emailValid ? 'Enter an email address we can reply to' : ' '}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Company" value={form.company} onChange={set('company')} fullWidth
            autoComplete="organization" inputProps={{ maxLength: 120 }}
          />
          <TextField select label="About" value={form.topic} onChange={set('topic')} fullWidth>
            {TOPICS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
        </Stack>
        <TextField
          label="Message" value={form.message} onChange={set('message')} required fullWidth multiline minRows={4}
          inputProps={{ maxLength: 4000 }}
          error={touched && !form.message.trim()}
          placeholder="What kind of work would you record, and roughly how many people would use it?"
        />
        {/* Honeypot: off-screen and out of the tab order, so only bots fill it. */}
        <Box aria-hidden="true" sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
          <TextField label="Website" value={form.website} onChange={set('website')} autoComplete="off" inputProps={{ tabIndex: -1 }} />
        </Box>
        {captcha.enabled && <div ref={captcha.containerRef} data-testid="turnstile-widget" />}
        {error && <Alert severity="error">{error}</Alert>}
        <Box>
          <Button
            type="submit" variant="contained" size="large" disabled={sending}
            endIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            sx={{ py: 1.4, px: 4, fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
          >
            {sending ? 'Sending…' : 'Send message'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export default ContactForm;
